import { Component, inject, Input, OnInit } from '@angular/core';
import { TransportIncidentService } from '../../../../services/transport-incident.service';
import { PATHS } from '../../../../models/bary-paths.model';
import { ITransportIncidentItem } from '../../../../models/transport-incident-item.model';
import { AbstractTransport } from '../../../../models/abstract-transport.model';
import { AlertService } from '../../../../services/alert.service';

import { Transporter } from '../../../../models/transporter.model';
import { TransportOrderExtendedService } from '../../../../services/transport-order-extended.service';
import { forkJoin } from 'rxjs';
import { HttpResponse } from '@angular/common/http';

import { TranslateService } from '@ngx-translate/core';
import { LIST_MAILTO } from 'app/shared/components/bary-mailto/bary-mailto.component';
import { UserMinimal } from '../../../../models/user-minimal.model';
import { BillService } from '../../../../services/bill-service';

import dayjs from 'dayjs';
import { TransportOrderView } from '../../../../models/transport-order-view.model';
import { TransportIncidentView } from '../../../../models/transport-incident-view.model';
import { PayzenTransporterConfigurationService } from '../../../../services/payzen-transporter-configuration.service';
import { CurrentDataService } from '../../../../services/current-data-service';
import { MailTransporterConfigurationService } from '../../../../services/mails-transporter-configuration.service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { EdiTransporterConfigurationService } from '../../../../services/edi-transporter-configuration.service';
import { mapAsBody } from '../../util/request-util';
import { PaymentMethodEnum } from '../../../../models/enums/payment-method.enum';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';
import { DocumentTransporterConfigurationService } from '../../../../services/document-transporter-configuration.service';
import { DocumentTransporterConfiguration } from '../../../../models/document-transporter.configuration';

@Component({
  selector: 'jhi-transport-view',
  templateUrl: './transport-view.component.html',
  styleUrls: ['./transport-view.component.scss'],
})
export class TransportViewComponent implements OnInit {
  private transportIncidentService = inject(TransportIncidentService);
  private transportOrderService = inject(TransportOrderExtendedService);
  private alertService = inject(AlertService);
  private payzenTransporterConfigurationService = inject(PayzenTransporterConfigurationService);
  private currentDataService = inject(CurrentDataService);
  private documentTransporterConfigurationService = inject(DocumentTransporterConfigurationService);
  private mailTransporterConfigurationService = inject(MailTransporterConfigurationService);
  private baryModalService = inject(BaryModalService);
  private translateService = inject(TranslateService);
  private ediTransporterConfigurationService = inject(EdiTransporterConfigurationService);
  private billService = inject(BillService);

  @Input() transportOrder!: TransportOrderView;
  @Input() showStickers = false;
  @Input() savMail?: string;
  @Input() middleView = false;

  public userMinimal: UserMinimal = new UserMinimal();
  public ediId?: string;

  public canDeclareIncident = false;
  public incidents: TransportIncidentView[] = [];
  public items: ITransportIncidentItem[] = [];
  public trackAndTraceLink?: string;
  public hasStickerFeature: boolean = false;
  public transporter?: Transporter;
  public isDisplayADRWarning = false;
  public mailto = '';
  public documentEmailTransporter?: string;
  public isInitTransfertImmediate = false;

  public LIST_MAILTO = LIST_MAILTO;
  protected readonly dayjs = dayjs;

  protected readonly FaDirectiveEnum = FaDirectiveEnum;
  protected readonly PATHS = PATHS;

  get abstractTransport(): AbstractTransport {
    return this.transportOrder as AbstractTransport;
  }

  public ngOnInit(): void {
    if (this.transportOrder.id) {
      forkJoin([
        this.documentTransporterConfigurationService.retrieveConfigurationFromCurrentTransporter(),
        this.transportOrderService.getHash(this.transportOrder.id),
        this.payzenTransporterConfigurationService.canAddIncident(this.transportOrder.id),
        this.mailTransporterConfigurationService.retrieveConfiguration().pipe(mapAsBody),
        this.ediTransporterConfigurationService.retrieveConfiguration().pipe(mapAsBody),
      ]).subscribe({
        next: (data: [HttpResponse<DocumentTransporterConfiguration>, HttpResponse<string>, HttpResponse<boolean>, any, any]) => {
          this.hasStickerFeature = !!data[0].body?.stickerPdfTemplate;
          this.trackAndTraceLink = 'https://' + window.location.host + '/track-and-trace/' + data[1];
          this.canDeclareIncident = data[2].body ?? false;
          this.isInitTransfertImmediate = this.transportOrder.paymentMethod === PaymentMethodEnum.INIT_TRANSFERT_IMMEDIATE;
          this.documentEmailTransporter = data[3].document.value ?? '';
          this.ediId = data[4]?.ediId;
        },
      });
    }
    this.currentDataService.getCurrentTransporter().subscribe(value => {
      if (value) {
        this.transporter = value!;
      }
    });
    this.getUserData();
    this.loadTransportIncidents();
    this.loadADRWaning();
  }

  private loadTransportIncidents(): void {
    if (this.transportOrder.id) {
      this.transportIncidentService
        .findAllForTransportOrder(this.transportOrder.id)
        .pipe(mapAsBody)
        .subscribe((resBody: any) => {
          this.incidents = resBody;
        });
    }
  }

  public openAddIncidentPopin(): void {
    this.baryModalService
      .openAddTransportIncident(this.transporter!.id!, this.transportOrder!.id!)
      .action()
      .subscribe(action => {
        if (action.submit) {
          this.loadTransportIncidents();
        } else if (action.failure) {
          this.alertService.addNotification({
            type: 'danger',
            message: 'Une erreur est survenue durant la déclaration de la prestation complémentaire.',
          });
        }
      });
  }

  public copyLink(): void {
    if (this.trackAndTraceLink) {
      navigator.clipboard.writeText(this.trackAndTraceLink).then(() =>
        this.alertService.addAlert({
          message: 'Lien copié !',
          type: 'info',
        })
      );
    }
  }

  public loadADRWaning(): void {
    const now = dayjs().startOf('day');
    if (this.transportOrder.isHazardousMaterial && !now.isAfter(this.transportOrder.pickupDate)) {
      this.isDisplayADRWarning = true;
      const subject = this.translateService.instant('mailto.add_document_adr.subject', { transportOrderId: this.transportOrder.id });
      const body = this.translateService.instant('mailto.add_document_adr.body', {
        companyName: this.transporter?.companyName,
        transportOrderId: this.transportOrder.id,
      });
      this.mailto = 'mailto:' + this.documentEmailTransporter + '?subject=' + subject + '&body=' + body;
    }
  }

  public getUserData(): any {
    this.userMinimal = new UserMinimal();
    this.currentDataService.getUser().subscribe(value => {
      this.userMinimal.firstName = value?.firstName;
      this.userMinimal.lastName = value?.lastName;
      this.userMinimal.email = value?.email;
      this.userMinimal.phone = value?.phone;
      this.userMinimal.companyName = value?.companyName;
    });
  }

  public clickToShowBill(basketOrderId: number): void {
    return this.billService.browserPdf(basketOrderId);
  }

  public openLink(podUrl: string): void {
    window.open(podUrl, '_blank');
  }

  deleteIncident(id: any): void {
    this.transportIncidentService.delete(id).subscribe({
      next: () => {
        this.loadTransportIncidents();
      },
    });
  }
}
